import React, { useState, useEffect, useRef } from 'react';
import { Building2, ImagePlus, Trash2 } from 'lucide-react';
import {
  PRECOS_PADRAO, salvarConfiguracao, enviarLogo, removerLogo, urlDaLogo,
  problemaDaLogo, TIPOS_DA_LOGO,
} from '../../../data/configuracaoDoLaboratorio';

const DADOS = [
  { key: 'cnpj',                label: 'CNPJ',                 placeholder: '00.000.000/0001-00' },
  { key: 'telefone',            label: 'Telefone',             placeholder: '(31) 99999-0000' },
  { key: 'endereco',            label: 'Endereço',             placeholder: 'Rua, número, cidade - UF', largo: true },
  { key: 'responsavel_tecnico', label: 'Responsável técnico',  placeholder: 'Nome do veterinário' },
  { key: 'responsavel_crmv',    label: 'CRMV do responsável',  placeholder: 'CRMV-MG 0000' },
];

/** O laboratório como o formulário edita: tudo texto, vazio em vez de nulo. */
const paraFormulario = (lab) => ({
  ...Object.fromEntries(DADOS.map(({ key }) => [key, lab?.[key] || ''])),
  entrada_por_embriao: lab?.entrada_por_embriao ?? '',
  sebrae: !!lab?.sebrae,
  sebrae_responsavel: lab?.sebrae_responsavel || '',
  precos: Object.fromEntries(PRECOS_PADRAO.map(({ key }) =>
    [key, lab?.precos_padrao?.[key] ?? ''])),
});

/** O formulário como a função do banco recebe. Preço vazio sai do padrão. */
const paraSalvar = (form) => ({
  ...Object.fromEntries(DADOS.map(({ key }) => [key, form[key]])),
  entrada_por_embriao: form.entrada_por_embriao === '' ? null : Number(form.entrada_por_embriao),
  sebrae: !!form.sebrae,
  sebrae_responsavel: form.sebrae ? form.sebrae_responsavel : null,
  precos_padrao: Object.fromEntries(Object.entries(form.precos)
    .filter(([, v]) => v !== '' && Number.isFinite(Number(v)))
    .map(([k, v]) => [k, Number(v)])),
});

const campo = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none '
  + 'focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500';

/**
 * Dados, logo e preços padrão do laboratório.
 *
 * Os dados e a logo vão para o cabeçalho dos relatórios. Os preços PREENCHEM
 * o formulário de serviço novo e ali continuam editáveis — nunca valem como
 * reserva na hora de cobrar.
 *
 * A logo grava na hora, sem passar pelo Salvar: é um arquivo que já subiu, e
 * deixar o caminho para depois do botão criaria a logo que está no bucket e
 * não está no laboratório.
 */
const ConfiguracaoDoLaboratorio = ({ laboratorio, podeMexer = false, onSalvo }) => {
  const [form, setForm] = useState(() => paraFormulario(laboratorio));
  const [salvando, setSalvando] = useState(false);
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');
  const arquivo = useRef(null);

  // Trocar de laboratório recarrega o formulário. Só pelo id: o mesmo
  // laboratório voltando salvo não pode apagar o que está sendo digitado.
  useEffect(() => {
    setForm(paraFormulario(laboratorio));
    setErro(''); setAviso('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratorio?.id]);

  const muda = (key, valor) => { setForm(f => ({ ...f, [key]: valor })); setAviso(''); };
  const mudaPreco = (key, valor) => {
    setForm(f => ({ ...f, precos: { ...f.precos, [key]: valor } })); setAviso('');
  };

  const salvar = async () => {
    setSalvando(true); setErro(''); setAviso('');
    try {
      const lab = await salvarConfiguracao(laboratorio.id, paraSalvar(form));
      setForm(paraFormulario(lab));
      onSalvo?.(lab);
      setAviso('Configuração salva.');
    } catch (e) {
      setErro(e.message);
    } finally { setSalvando(false); }
  };

  const escolherLogo = async (e) => {
    const escolhido = e.target.files?.[0];
    e.target.value = '';
    if (!escolhido) return;
    const problema = problemaDaLogo(escolhido);
    if (problema) { setErro(problema); return; }

    setEnviandoLogo(true); setErro(''); setAviso('');
    try {
      const lab = await enviarLogo(laboratorio.id, escolhido, laboratorio.logo_path);
      onSalvo?.(lab);
      setAviso('Logo atualizada. Os próximos relatórios já saem com ela.');
    } catch (err) {
      setErro(err.message);
    } finally { setEnviandoLogo(false); }
  };

  const tirarLogo = async () => {
    if (!window.confirm('Remover a logo? Os relatórios voltam a sair com a logo do FIVPro.')) return;
    setEnviandoLogo(true); setErro(''); setAviso('');
    try {
      const lab = await removerLogo(laboratorio.id, laboratorio.logo_path);
      onSalvo?.(lab);
    } catch (err) {
      setErro(err.message);
    } finally { setEnviandoLogo(false); }
  };

  const logo = urlDaLogo(laboratorio?.logo_path);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="px-6 py-4 border-b flex items-center space-x-3">
        <Building2 className="text-slate-400" size={20} />
        <div>
          <h3 className="text-lg font-medium text-slate-900">Dados do laboratório</h3>
          <p className="text-sm text-slate-600">
            Saem no cabeçalho dos relatórios. Os preços preenchem o cadastro de serviço novo.
          </p>
        </div>
      </div>

      <div className="px-6 py-5 space-y-6">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className="w-40 h-20 border border-dashed border-slate-300 rounded-lg flex items-center
                          justify-center bg-slate-50 shrink-0 overflow-hidden">
            {logo
              ? <img src={logo} alt="Logo do laboratório" className="max-h-16 max-w-[9rem] object-contain" />
              : <span className="text-xs text-slate-400 text-center px-2">Sem logo — os relatórios usam a do FIVPro</span>}
          </div>
          {podeMexer && (
            <div className="space-y-2">
              <input ref={arquivo} type="file" accept={TIPOS_DA_LOGO.join(',')}
                onChange={escolherLogo} className="hidden" />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => arquivo.current?.click()} disabled={enviandoLogo}
                  className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm
                             text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  <ImagePlus size={16} />
                  {enviandoLogo ? 'Enviando…' : logo ? 'Trocar logo' : 'Enviar logo'}
                </button>
                {logo && (
                  <button onClick={tirarLogo} disabled={enviandoLogo}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-500
                               hover:text-slate-800 hover:bg-slate-50 disabled:opacity-40">
                    <Trash2 size={14} /> Remover
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">PNG, JPG ou WebP até 2 MB. PNG com fundo transparente fica melhor.</p>
            </div>
          )}
        </div>

        {/* Dados cadastrais */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DADOS.map(d => (
            <label key={d.key} className={d.largo ? 'sm:col-span-2' : ''}>
              <span className="block text-sm font-medium text-slate-700 mb-1">{d.label}</span>
              <input type="text" value={form[d.key]} placeholder={d.placeholder}
                disabled={!podeMexer}
                onChange={(e) => muda(d.key, e.target.value)} className={campo} />
            </label>
          ))}
        </div>

        {/* Programa SEBRAE. O responsável só aparece com a flag: fora do
            programa ele não significa nada. */}
        {/* A caixa tem rótulo em cima e a altura de um input, como o campo ao
            lado — senão as duas não alinham. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-1">SEBRAE</span>
            <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm
                              text-slate-700 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={form.sebrae} disabled={!podeMexer}
                onChange={(e) => muda('sebrae', e.target.checked)} className="h-4 w-4" />
              Laboratório vinculado ao programa
            </label>
          </div>
          {form.sebrae && (
            <label>
              <span className="block text-sm font-medium text-slate-700 mb-1">Responsável pelo projeto</span>
              <input type="text" value={form.sebrae_responsavel} placeholder="Nome que sai nos relatórios"
                disabled={!podeMexer}
                onChange={(e) => muda('sebrae_responsavel', e.target.value)} className={campo} />
              <span className="block text-xs text-slate-400 mt-1">
                Sai como consultor(a) em todos os relatórios SEBRAE, mesmo quando outro técnico executou.
              </span>
            </label>
          )}
        </div>

        {/* Preços padrão */}
        <div>
          <p className="text-sm font-medium text-slate-900">Preços padrão</p>
          <p className="text-xs text-slate-500 mb-3">
            Já vêm preenchidos no cadastro de serviço novo e podem ser mudados lá.
            Mudar aqui não altera serviço já cadastrado. Vazio = sem padrão.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label>
              <span className="block text-xs font-medium text-slate-600 mb-1">Entrada por embrião transferido</span>
              <input type="number" min="0" step="0.01" value={form.entrada_por_embriao}
                disabled={!podeMexer}
                onChange={(e) => muda('entrada_por_embriao', e.target.value)} className={campo} />
            </label>
            {PRECOS_PADRAO.map(p => (
              <label key={p.key}>
                <span className="block text-xs font-medium text-slate-600 mb-1">{p.label}</span>
                <input type="number" min="0" step="0.01" value={form.precos[p.key]}
                  disabled={!podeMexer}
                  onChange={(e) => mudaPreco(p.key, e.target.value)} className={campo} />
              </label>
            ))}
          </div>
        </div>

        {erro && (
          <div className="bg-white border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">{erro}</div>
        )}

        {podeMexer && (
          <div className="flex items-center gap-3">
            <button onClick={salvar} disabled={salvando}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-40">
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            {aviso && <span className="text-sm text-slate-600">{aviso}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfiguracaoDoLaboratorio;
